/**
 * Maps legacy FA icon class names to react-feather components.
 * Used by data-driven components (badges, options lists, notifications)
 * that store icon identifiers as strings.
 */
import {
  Heart, Star, MapPin, User, UserPlus, UserCheck, Users,
  Edit2, Edit3, MessageCircle, MessageSquare, Mail,
  Clock, Calendar, PlusCircle, Gift, Phone, Briefcase,
  Zap, Bell, BellOff, Sun, Smile, MoreHorizontal,
  Home, Feather, Volume2, Cpu, List, BookOpen,
  Send, CornerUpLeft, CornerUpRight, Coffee, Award,
} from 'react-feather';
import type { ComponentType } from 'react';

type FeatherIcon = ComponentType<{ size?: number | string; color?: string; className?: string }>;

const ICON_MAP: Record<string, FeatherIcon> = {
  // People & profiles
  'fa-user': User,
  'fa-user-check': UserCheck,
  'fa-user-plus': UserPlus,
  'fa-user-group': Users,
  'fa-users': Users,

  // Communication
  'fa-heart': Heart,
  'fa-hand-holding-heart': Heart,
  'fa-comments': MessageCircle,
  'fa-comment-dots': MessageSquare,
  'fa-envelope': Mail,
  'fa-paper-plane': Send,
  'fa-reply': CornerUpLeft,
  'fa-reply-all': CornerUpRight,
  'fa-phone': Phone,
  'fa-bullhorn': Volume2,

  // Places & navigation
  'fa-map-marker-alt': MapPin,
  'fa-map-pin': MapPin,
  'fa-house-chimney': Home,

  // Time & events
  'fa-clock': Clock,
  'fa-calendar': Calendar,
  'fa-calendar-alt': Calendar,
  'fa-calendar-plus': PlusCircle,

  // Actions & editing
  'fa-pen': Edit2,
  'fa-pen-fancy': Edit3,

  // Content & status
  'fa-star': Star,
  'fa-gift': Gift,
  'fa-lightbulb': Sun,
  'fa-bolt': Zap,
  'fa-bell': Bell,
  'fa-bell-slash': BellOff,
  'fa-briefcase': Briefcase,
  'fa-seedling': Feather,
  'fa-brain': Cpu,
  'fa-list-check': List,
  'fa-mug-hot': Coffee,
  'fa-smile': Smile,
  'fa-ellipsis': MoreHorizontal,
  'fa-award': Award,

  // Aliases used with 'fas ' prefix in type data
  'fas fa-mug-hot': Coffee,
  'fas fa-heart': Heart,
  'fas fa-hand-holding-heart': Heart,
  'fas fa-champagne-glasses': Gift,
  'fas fa-hands-helping': Users,
  'fas fa-people-arrows': Users,
  'fas fa-rotate': CornerUpLeft,
  'fas fa-dove': Send,
  'fas fa-gift': Gift,
  'fas fa-phone': Phone,
  'fas fa-calendar-plus': PlusCircle,
  'fas fa-newspaper': BookOpen,
  'fas fa-lightbulb': Sun,
  'fas fa-clock': Clock,
  'fas fa-comment-dots': MessageSquare,
  'fas fa-user-plus': UserPlus,
  'fas fa-briefcase': Briefcase,
  'fas fa-smile': Smile,
  'fas fa-ellipsis': MoreHorizontal,
  'fas fa-house-chimney': Home,
  'fas fa-user-group': Users,
  'fas fa-seedling': Feather,
  'fas fa-bullhorn': Volume2,
  'fas fa-list-check': List,
  'fas fa-brain': Cpu,
};

/**
 * Look up a Feather icon component by FA class name string.
 * Returns a fallback (Star) if no match is found.
 */
export function getFeatherIcon(faName: string): FeatherIcon {
  // Try direct lookup first
  if (ICON_MAP[faName]) return ICON_MAP[faName];

  // Strip 'fas ', 'far ', 'fab ' prefix and retry
  const stripped = faName.replace(/^fa[srb]\s+/, '');
  if (ICON_MAP[stripped]) return ICON_MAP[stripped];

  return Star; // fallback
}

export default ICON_MAP;
